import { MixBlend, MixDirection, Physics } from "@esotericsoftware/spine-core";

/**
 * spineMeshWarm — build a parked spine's render meshes for EVERY clip, ahead
 * of its first real use (2026-10-07, Vladimir: "the game stutters a tiny bit
 * … check if all spines are ready and warmed up").
 *
 * spine-pixi creates a slot's mesh the first time that slot draws an
 * attachment (Spine.getMeshForSlot). The overlay cells' boot warm-up only
 * ever drew their SETUP pose (its pose was never applied, and the bare
 * updateWorldTransform() threw in Spine 4.2), so every symbol built the
 * meshes of its landing / win / wait clips on its first landing in each
 * cell — mid-spin. The Wild (41 slots, none drawn in setup) worst of all.
 *
 * What this does, per spine, a few spines per frame so it never adds a hitch
 * of its own (the free-spins board builds its cells inside the entry wipe):
 *
 *   every clip   applied to the SKELETON directly at its middle frame
 *                (no AnimationState track is touched), world transform,
 *                renderMeshes() — the meshes now exist;
 *   then         back to the setup pose — the state a parked cell rests in.
 *
 * A spine that is busy when its turn comes (`isBusy`) is skipped: it is in
 * use, and its own clips are building what they need anyway.
 */

// Spines warmed per frame.
const PER_FRAME = 6;

const queue = [];
let scheduled = false;

const physicsArg = (skeleton) =>
  skeleton.physics !== undefined ? Physics.update : undefined;

const warmOne = ({ spine, isBusy }) => {
  if (!spine || spine.destroyed || !spine.skeleton || isBusy?.()) return;
  const { skeleton } = spine;
  const animations = skeleton.data?.animations || [];
  try {
    for (const anim of animations) {
      skeleton.setToSetupPose();
      anim.apply(
        skeleton,
        0,
        anim.duration * 0.5,
        false,
        [],
        1,
        MixBlend.setup,
        MixDirection.mixIn,
      );
      skeleton.updateWorldTransform(physicsArg(skeleton));
      spine.renderMeshes?.();
    }
  } catch {
    // Warm-up is an optimisation — never let it stop anything.
  } finally {
    try {
      skeleton.setToSetupPose();
      skeleton.updateWorldTransform(physicsArg(skeleton));
    } catch {
      // ignore
    }
  }
};

const drain = () => {
  scheduled = false;
  for (let n = 0; n < PER_FRAME && queue.length; n++) warmOne(queue.shift());
  if (queue.length) {
    scheduled = true;
    requestAnimationFrame(drain);
  }
};

/**
 * Queue one parked spine.
 * @param {object}   job
 * @param {object}   job.spine   The spine-pixi Spine.
 * @param {Function} [job.isBusy] True when it must be left alone now.
 */
export const queueMeshWarm = (job) => {
  if (!job?.spine) return;
  queue.push(job);
  if (!scheduled) {
    scheduled = true;
    requestAnimationFrame(drain);
  }
};
